/**
 * Inline form error announcements (role="alert") for auth/account forms.
 * Prefer this over window.alert() for WCAG 3.3.1 / 3.3.3.
 */

/**
 * Show or update an inline error on a form.
 * @param {HTMLFormElement|HTMLElement} form
 * @param {string} message
 * @param {{ fieldIds?: string[] }} [options]
 */
export function showFormError(form, message, { fieldIds = [] } = {}) {
  if (!form) {
    window.alert(message);
    return;
  }

  let region = form.querySelector('[data-form-error]');
  if (!region) {
    region = document.createElement('div');
    region.setAttribute('data-form-error', '1');
    region.setAttribute('role', 'alert');
    region.className = 'form-error-alert';
    region.style.cssText =
      'margin:0 0 12px;padding:10px 12px;border-radius:8px;background:#fef2f2;border:1px solid #fecaca;color:#991b1b;font-size:0.9375rem;';
    form.prepend(region);
  }
  region.textContent = message;
  region.hidden = false;

  const inputs = form.querySelectorAll('input, select, textarea');
  inputs.forEach((el) => {
    el.removeAttribute('aria-invalid');
    const describedby = el.getAttribute('aria-describedby');
    if (describedby && region.id && describedby.split(/\s+/).includes(region.id)) {
      el.setAttribute(
        'aria-describedby',
        describedby
          .split(/\s+/)
          .filter((id) => id !== region.id)
          .join(' ')
          .trim() || null,
      );
    }
  });

  if (!region.id) {
    region.id = `${form.id || 'form'}-error`;
  }

  for (const id of fieldIds) {
    const field = form.querySelector(`#${CSS.escape(id)}`) || document.getElementById(id);
    if (!field) continue;
    field.setAttribute('aria-invalid', 'true');
    const existing = (field.getAttribute('aria-describedby') || '')
      .split(/\s+/)
      .filter(Boolean);
    if (!existing.includes(region.id)) {
      existing.push(region.id);
      field.setAttribute('aria-describedby', existing.join(' '));
    }
  }

  region.focus?.();
}

/**
 * Clear inline form error state.
 * @param {HTMLFormElement|HTMLElement} form
 */
export function clearFormError(form) {
  if (!form) return;
  const region = form.querySelector('[data-form-error]');
  if (region) {
    region.textContent = '';
    region.hidden = true;
  }
  form.querySelectorAll('[aria-invalid="true"]').forEach((el) => {
    el.removeAttribute('aria-invalid');
  });
}
